import { TCAppConfig } from "../../../../../../shared/app-configs/tc-app-configs";
import { IncomingTCInformation, IncomingProductNetWeightInfo } from "../../../tc-models/tc-req-dto/incoming-details.req.dto";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import {
  filterCertifiedIncomingRawMaterials,
  mergeIncomingRawMaterialsForDisplay,
  withIncomingRawMaterialKeys,
} from "./incoming-raw-material.utils";
import { roundTcV4Decimal } from "../../../tc-v4-decimals";

export interface IncomingTcEntry {
  id: number;
  incomingTcId?: string;
  productId?: string;
  incomingTcInfo: IncomingTCInformation;
  productNetWeight: IncomingProductNetWeightInfo;
  inputTcStandard?: Array<{
    incomingProductArticleNo: string;
    incomingProductStandard: string[];
    incomingTcStandardDescription?: string[];
  }>;
}

export interface IncomingTcPayload {
  tcId: string;
  incomingTcInfo: IncomingTCInformation;
  productNetWeight: IncomingProductNetWeightInfo;
}

const normalizeStringArray = (value: any): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item === undefined || item === null) return "";
        return String(item).trim();
      })
      .filter((val) => Boolean(val));
  }
  if (value === undefined || value === null) return [];

  if (typeof value === "string" && value.includes(",")) {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter((val) => Boolean(val));
  }
  const normalized = String(value).trim();
  return normalized ? [normalized] : [];
};

const uniqueStrings = (values: string[]): string[] => {
  return Array.from(new Set(values.map((val) => String(val).trim()).filter((val) => Boolean(val))));
};

export const standardCodeFromValue = (value: any): string => {
  const text = String(value || "").trim();
  if (!text) return "";

  const parenthesizedCode = text.match(/\(([A-Z]{2,5})\)/);
  if (parenthesizedCode?.[1]) {
    return parenthesizedCode[1].toUpperCase();
  }

  const upperText = text.toUpperCase();
  const labelCodeMap: Record<string, string> = {
    "GLOBAL RECYCLED STANDARD": "GRS",
    "RECYCLED CLAIM STANDARD": "RCS",
    "ORGANIC CONTENT STANDARD": "OCS",
    "RESPONSIBLE WOOL STANDARD": "RWS",
    "RESPONSIBLE MOHAIR STANDARD": "RMS",
    "RESPONSIBLE ALPACA STANDARD": "RAS",
    "RESPONSIBLE DOWN STANDARD": "RDS",
    "CONTENT CLAIM STANDARD": "CCS",
    "MATERIALS MATTER": "MMS",
    "MATERIAL MATTER": "MMS",
    "GLOBAL ORGANIC TEXTILE STANDARD": "GOTS",
  };

  const matchedLabel = Object.keys(labelCodeMap).find((label) => upperText.includes(label));
  if (matchedLabel) {
    return labelCodeMap[matchedLabel];
  }

  if (/^[A-Z]{2,5}$/.test(upperText)) {
    return upperText;
  }

  const compact = upperText.replace(/[^A-Z]/g, "");
  if (compact.startsWith("GOTS")) return "GOTS";
  return compact.length >= 3 ? compact.slice(0, 3) : compact;
};

const normalizeStandardCodes = (value: any): string[] =>
  uniqueStrings(
    normalizeStringArray(value)
      .flatMap((item) => String(item).split(","))
      .map(standardCodeFromValue)
      .filter(Boolean)
  );

const roundWeight = (value: number): number => roundTcV4Decimal(Number(value) || 0);

// RMDF/FPF/ATU/GOTS numbers may carry the type token anywhere in the value
// (e.g. "RMDF_CUC_12344" or "CUC_RMDF_12345"), so match whole separator-split
// tokens instead of only the leading characters.
export const hasCertTypeToken = (value: any, ...types: string[]): boolean => {
  if (!value) return false;
  try {
    const tokens = String(value).toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);
    return types.some((type) => tokens.includes(type));
  } catch {
    return false;
  }
};
const getRawMaterialKey = (rm: any, index: number): string =>
  [
    rm.tcProductCategoryCode || "",
    rm.tcProductDetailCode || "",
    rm.rawMaterialCode || rm.tcProductIncomingCertifiedRawMaterialCode || "",
    Number(rm.rawMaterialPercentage || 0),
    index,
  ].join("|");

const toNumber = (value: any): number => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const unwrapIncomingProducts = (payload: any): any[] => {
  if (!payload) return [];
  let data = payload;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return [];
    }
  }
  if (data?.response !== undefined) return unwrapIncomingProducts(data.response);
  const rows = Array.isArray(data) ? data : [data];
  return rows
    .flatMap((row: any) => {
      const value = row?.trackitPortalData ?? row;
      return Array.isArray(value) ? value : [value];
    })
    .filter(Boolean);
};

const mapIncomingProductToRawRows = (product: any): any[] => {
  const rawMaterials = Array.isArray(product?.tcProductRawMaterialInformation)
    ? product.tcProductRawMaterialInformation
    : [];
  const netWeight = toNumber(product?.tcProductNetShippingWeight);
  const supplementaryWeight = toNumber(product?.tcProductSupplementaryWeight);
  const additivesWeight = toNumber(product?.tcProductAdditivesWeight);

  return rawMaterials.map((item: any) => ({
    tcProductCategoryCode: product?.tcProductCategoryCode || "",
    tcProductDetailCode: product?.tcProductDetailCode || "",
    rawMaterialCode: item?.tcProductRawMaterialCode || item?.rawMaterialCode || "",
    rawMaterialPercentage: toNumber(item?.tcProductRawMaterialPercentage || item?.rawMaterialPercentage) || 100,
    isCertified: item?.isCertified ?? item?.is_certified ?? "",
    tcProductNetShippingWeight: netWeight,
    tcProductSupplementaryWeight: supplementaryWeight,
    tcProductAdditivesWeight: additivesWeight,
  }));
};

const calculateIncomingRmWeight = (netWeightUsed: number, percentage: number, rm: any): number => {
  const sourceNet = Number(rm.tcProductNetShippingWeight || 0);
  const sourceSupplementary = Number(rm.tcProductSupplementaryWeight || 0);
  const sourceAdditives = Number(rm.tcProductAdditivesWeight || 0);
  const supplementaryUsed = sourceNet > 0 ? (sourceSupplementary / sourceNet) * netWeightUsed : 0;
  const additivesUsed = sourceNet > 0 ? (sourceAdditives / sourceNet) * netWeightUsed : 0;
  const productWeightForSplit = Math.max(Number(netWeightUsed || 0) - supplementaryUsed - additivesUsed, 0);
  return roundWeight((Number(percentage) / 100) * productWeightForSplit);
};

export async function fetchIncomingTcDetailsList(
  executeGet: (url: string, params?: Record<string, any>, config?: Record<string, any>) => Promise<any>,
  tcId: string,
  limit: number = 10,
  offset: number = 0
): Promise<{ entries: IncomingTcEntry[]; totalCount: number }> {
  try {
    const response = await executeGet(TCAppConfig.GET_INCOMING_DETAIL_LIST_V4, { 
      tcId,
      limit,
      offset
    });
    const data = response?.data?.response || response?.response || [];
    const totalCount = response?.data?.no_of_records || response?.no_of_records || data.length;

    const isAtcValue = (val: any) => hasCertTypeToken(val, 'GOTS', 'ATU');
    const isRmdfValue = (val: any) => hasCertTypeToken(val, 'RMDF');
    const isFpfValue = (val: any) => hasCertTypeToken(val, 'FPF');

    const mappedData = data.map((item: any, index: number) => {
      // Handle nested structure: if item has incomingTcProductInformation array, use first element
      const baseItem = Array.isArray(item.incomingTcProductInformation) && item.incomingTcProductInformation.length > 0
        ? { ...item, ...item.incomingTcProductInformation[0] }
        : item;

      const incomingTcId = item.incomingTcId || item.incoming_tc_id || item.id || undefined;
      const productId = item.productId || item.product_id || item.tcProductId || undefined;

      const rawIncomingNo = baseItem.tcProductIncomingTcNo || baseItem.incomingTcNo || baseItem.incoming_tc_no || '';
      const rawAtcNo = baseItem.incomingAtcNo || baseItem.incoming_atc_no || '';
      const rawRmdfNo = baseItem.incomingRmdfNo || baseItem.incoming_rmdf_no || '';

      const incomingIsAtc = isAtcValue(rawIncomingNo);
      const incomingIsRmdf = isRmdfValue(rawIncomingNo);
      const incomingIsFpf = isFpfValue(rawIncomingNo);

      const tcIncomingATCNo = incomingIsAtc ? String(rawIncomingNo) : (rawAtcNo || '');
      const tcIncomingRMDFNo = incomingIsRmdf ? String(rawIncomingNo) : (rawRmdfNo || '');
      const tcIncomingFPFNo = incomingIsFpf ? String(rawIncomingNo) : '';
      const tcIncomingTCNo = (!incomingIsAtc && !incomingIsRmdf && !incomingIsFpf) ? (rawIncomingNo || '') : '';
      const incomingV4Issued = String(baseItem.tcProductIncomingTcV4Issued || "").toUpperCase();
      const tcIncomingType = incomingIsFpf
        ? "FPF"
        : incomingIsRmdf
        ? "RMDF"
        : incomingIsAtc
        ? (hasCertTypeToken(rawIncomingNo, "GOTS") ? "GOTS" : "ATU")
        : tcIncomingTCNo
        ? (incomingV4Issued === "Y" ? "V4" : "V3")
        : "";

      const weightInfoList = baseItem.incomingTcProductNetWeightUsedInformation || baseItem.inputTcProductNetWeightUsedInformation || [];

      const weightBasedStandards = Array.isArray(weightInfoList)
        ? weightInfoList.map((weightInfo: any) => ({
            incomingProductArticleNo: weightInfo.tcProductIncomingArticleNo || weightInfo.incomingArticleNo || weightInfo.articleNo || "",
            incomingProductStandard: normalizeStringArray(weightInfo.tcProductIncomingTcStandard || weightInfo.incomingProductStandard || weightInfo.incoming_tc_standard),
            incomingTcStandardDescription: normalizeStringArray(weightInfo.tcProductIncomingTcStandard || weightInfo.incomingTcStandardDescription || weightInfo.incoming_tc_standard_description),
          })).filter((entry) => Boolean(entry.incomingProductArticleNo))
        : [];

      const legacyInputTcStandardList = item.inputTcStandard || item.input_tc_standard || [];
      const legacyStandards = Array.isArray(legacyInputTcStandardList)
        ? legacyInputTcStandardList.map((stdItem: any) => {
            // Handle case where inputTcStandard is a simple array of strings like ["GRS"]
            if (typeof stdItem === 'string') {
              return {
                incomingProductArticleNo: baseItem.tcAssociatedProductArticleNo || baseItem.tc_product_article_no || "",
                incomingProductStandard: normalizeStringArray([stdItem]),
                incomingTcStandardDescription: normalizeStringArray([stdItem]),
              };
            }
            // Handle case where inputTcStandard is an array of objects
            return {
              incomingProductArticleNo: stdItem.incomingProductArticleNo || stdItem.incoming_product_article_no || "",
              incomingProductStandard: normalizeStringArray(stdItem.incomingProductStandard || stdItem.incoming_product_standard),
              incomingTcStandardDescription: normalizeStringArray(stdItem.incomingTcStandardDescription || stdItem.incoming_tc_standard_description),
            };
          }).filter((entry) => Boolean(entry.incomingProductArticleNo))
        : [];

      const inputTcStandard = weightBasedStandards.length > 0 ? weightBasedStandards : legacyStandards;
      const productArticlesFromStandards = uniqueStrings(
        inputTcStandard.map((entry) => entry.incomingProductArticleNo || "")
      );

      // Extract article numbers from weight info if not already found
      if (!productArticlesFromStandards.length && Array.isArray(weightInfoList)) {
        weightInfoList.forEach((weightInfo: any) => {
          const articleNo = weightInfo.tcProductIncomingArticleNo || weightInfo.incomingArticleNo || weightInfo.articleNo;
          if (articleNo && !productArticlesFromStandards.includes(String(articleNo))) {
            productArticlesFromStandards.push(String(articleNo));
          }
        });
      }

      const incomingProductNoRaw = baseItem.incomingProductArticleNo || baseItem.incoming_product_article_no || baseItem.incomingArticleNo || null;
      let incomingProductNo: string[] = productArticlesFromStandards.length > 0 ? productArticlesFromStandards : [];

      if (!incomingProductNo.length) {
        if (Array.isArray(incomingProductNoRaw)) {
          incomingProductNo = incomingProductNoRaw.map((value: any) => String(value));
        } else if (incomingProductNoRaw) {
          incomingProductNo = [String(incomingProductNoRaw)];
        }
      }

      const aggregatedStandards = uniqueStrings(
        inputTcStandard.reduce<string[]>((acc, entry) => {
          if (Array.isArray(entry.incomingProductStandard)) {
            acc.push(...entry.incomingProductStandard);
          }
          return acc;
        }, [])
      );
      return {
        id: index,
        incomingTcId,
        productId,
        incomingTcInfo: {
          tcAssociatedProductArticleNo: baseItem.tcAssociatedProductArticleNo || baseItem.tc_product_article_no || baseItem.tcProductArticleNo || "",
          tcIncomingTCNo,
          tcIncomingATCNo,
          tcIncomingRMDFNo,
          tcIncomingFPFNo,
          incomingTcLicensingCode: baseItem.incomingTcLicensingCode || "",
          tcIncomingTCStandard: aggregatedStandards.length > 0
            ? aggregatedStandards
            : normalizeStringArray(baseItem.incomingTcStandard || baseItem.incoming_tc_standard),
          tcIncomingTCStandardDescription: normalizeStringArray(
            baseItem.incomingTcStandardDescription || baseItem.incoming_tc_standard_description || aggregatedStandards
          ),
          tcIncomingProductNo: incomingProductNo,
          tcIncomingTcId: baseItem.incomingTcNoId || baseItem.incoming_tc_no_id || "",
          tcIncomingAtcId: "",
          tcIncomingLedgerTransactionId: "",
          tcIncomingType: tcIncomingType as any,
          tcProductIncomingTcV4Issued: incomingV4Issued === "Y" ? "Y" : incomingV4Issued === "N" ? "N" : "",
          isFlagSeller: (baseItem.isSellerFarm === 'Yes' || baseItem.isSeller === 'Yes' || baseItem.isFlagSeller === 'Yes') ? 'Yes' : 'No',
        },
        productNetWeight: {
          netWeights: {},
          rawMaterials: {},
        },
        inputTcStandard,
      } as IncomingTcEntry;
    });

    return {
      entries: mappedData,
      totalCount
    };
  } catch (err: any) {
    console.error("Error fetching incoming TC details list:", err);

    // If backend signals "no records" in an error response, treat it as an empty list
    const noRecords =
      err?.response?.data?.no_of_records === 0 ||
      (Array.isArray(err?.response?.data?.response) && err.response.data.response.length === 0);

    if (noRecords) {
      return {
        entries: [],
        totalCount: 0
      };
    }

    throw new Error("Failed to load incoming TC details list.");
  }
}


export async function fetchIncomingTcDetail(
    executeGet: (url: string, params?: Record<string, any>, config?: Record<string, any>) => Promise<any>,
    tcId: string,
    incomingTcId: string,
    productId: string
  ): Promise<IncomingTcEntry | null> {

    if (!incomingTcId && !productId) return null;
  
    try {
       
        const params: Record<string, any> = { tcId };
        if (productId) params.productId = productId;
        const config: Record<string, any> = {};

        let effectiveIncomingTcId = incomingTcId;
        if (!effectiveIncomingTcId && productId) {
          try {
            const listResp = await executeGet(TCAppConfig.GET_INCOMING_DETAIL_LIST_V4, { tcId });
            const listData: any[] = listResp?.data?.response || listResp?.response || [];
       
            const found = listData.find((it: any) => {
              const pid = it.productId || it.product_id || it.tcProductId || it.tc_product_id;
              if (pid && pid === productId) return true;
              const assocArticle = it.tcAssociatedProductArticleNo || it.tc_associated_product_article_no || it.incomingProductArticleNo || it.incoming_product_article_no;
              if (assocArticle && (Array.isArray(assocArticle) ? assocArticle.includes(productId) : assocArticle === productId)) return true;
              const incomingNo = it.incomingTcNo || it.incoming_tc_no;
              if (incomingNo && incomingNo === productId) return true;
              return false;
            });
            if (found) {
              const incomingKey = Object.keys(found).find(k => k && k.toString().trim().toLowerCase() === 'incomingtcid');
              effectiveIncomingTcId = (incomingKey ? found[incomingKey] : undefined) || found.incomingTcId || found.incoming_tc_id || found.id || found.incomingId || undefined;
              if (effectiveIncomingTcId && typeof effectiveIncomingTcId === 'string') {
                effectiveIncomingTcId = effectiveIncomingTcId.trim();
              }
            }
          } catch (e) {
            // ignore fallback discovery errors
          }
        }

        if (effectiveIncomingTcId) {
       
          config.headers = { incomingTcId: effectiveIncomingTcId };
    
          params.incomingTcId = effectiveIncomingTcId;
        }

        const response = await executeGet(TCAppConfig.GET_INCOMING_DETAIL_V4, params, config);
  
 
      const respData = response?.data || response;


      const incomingInfoArr =
        respData?.incomingTcProductInformation ||
        respData?.inputTcInformation ||
        respData?.data?.inputTcInformation ||
        respData?.incoming_tc_information ||
        respData?.incomingTcInformationList ||
        null;

      if (!incomingInfoArr || incomingInfoArr.length === 0) return null;

  const tcInfo = incomingInfoArr[0];
  
      const netWeights: Record<string, number> = {};
      const rawMaterials: Record<
        string,
        Array<{
          tcProductCategoryCode: string;
          tcProductDetailCode: string;
          rawMaterialCode: string;
          rawMaterialPercentage: number;
          rawMaterialWeight: number;
          rawMaterialKey?: string;
          isCertified?: string;
          tcProductNetShippingWeight?: number;
          tcProductSupplementaryWeight?: number;
          tcProductAdditivesWeight?: number;
        }>
      > = {};
      const rawMaterialWeights: Record<string, Record<string, number>> = {};
      const standardsByArticle: Record<string, string[]> = {};

      const weightInfoList =
        tcInfo.incomingTcProductNetWeightUsedInformation ||
        tcInfo.inputTcProductNetWeightUsedInformation ||
        tcInfo.incoming_tc_product_net_weight_used_information ||
        tcInfo.incomingTcProductNetWeight ||
        null;

      let derivedInputTcStandard: Array<{
        incomingProductArticleNo: string;
        incomingProductStandard: string[];
        incomingTcStandardDescription: string[];
      }> = [];

      if (Array.isArray(weightInfoList)) {
        derivedInputTcStandard = weightInfoList
          .map((weightInfo: any) => {
            const articleNo = weightInfo.tcProductIncomingArticleNo || weightInfo.incomingArticleNo || weightInfo.articleNo || "";
            if (!articleNo) return null;

            const rawNetWeight =
              weightInfo.tcProductIncomingArticleNetWeightUsed ??
              weightInfo.incomingTcProductNetWeightUsed ??
              weightInfo.incoming_tc_product_net_weight_used ??
              weightInfo.incomingTcProductNetWeight ??
              0;
            const numericWeight = Number(rawNetWeight) || 0;
            if (numericWeight > 0) {
              netWeights[articleNo] = numericWeight;
            }

            const rawList =
              weightInfo.tcProductIncomingRawMaterialInformation ||
              weightInfo.incomingTcRawMaterialInformation ||
              weightInfo.incoming_tc_raw_material_information ||
              weightInfo.incomingTcRawMaterial ||
              weightInfo.rawMaterials ||
              [];

            if (Array.isArray(rawList) && rawList.length > 0) {
              rawMaterials[articleNo] = rawList.map((rm: any, index: number) => {
                const row = {
                  tcProductCategoryCode:
                    rm.tcProductCategoryCode ||
                    rm.tc_product_category_code ||
                    weightInfo.tcProductIncomingProductCategoryCode ||
                    "",
                  tcProductDetailCode:
                    rm.tcProductDetailCode ||
                    rm.tc_product_detail_code ||
                    weightInfo.tcProductIncomingProductDetailCode ||
                    "",
                  rawMaterialCode:
                    rm.rawMaterialCode ||
                    rm.raw_material_code ||
                    rm.tcProductIncomingCertifiedRawMaterialCode ||
                    rm.code ||
                    "",
                  rawMaterialPercentage: Number(rm.rawMaterialPercentage || rm.raw_material_percentage || 0),
                  rawMaterialWeight: Number(
                    rm.rawMaterialWeight ||
                    rm.raw_material_weight ||
                    rm.tcProductIncomingCertifiedRawMaterialWeightUsed ||
                    0
                  ),
                  isCertified:
                    rm.isCertified ??
                    rm.is_certified ??
                    (rm.tcProductIncomingCertifiedRawMaterialCode ? "Y" : ""),
                };
                const rawMaterialKey = getRawMaterialKey(row, index);
                if (row.rawMaterialWeight > 0) {
                  rawMaterialWeights[articleNo] = {
                    ...(rawMaterialWeights[articleNo] || {}),
                    [rawMaterialKey]: row.rawMaterialWeight,
                  };
                }
                return {
                  ...row,
                  rawMaterialKey,
                };
              });
            } else {
              rawMaterials[articleNo] = [];
            }

            const standardsForArticle = normalizeStringArray(
              weightInfo.tcProductIncomingTcStandard ||
              weightInfo.incomingProductStandard ||
              weightInfo.incoming_tc_standard
            );

            if (standardsForArticle.length > 0) {
              standardsByArticle[articleNo] = standardsForArticle;
            }

            return {
              incomingProductArticleNo: articleNo,
              incomingProductStandard: standardsForArticle,
              incomingTcStandardDescription: normalizeStringArray(
                weightInfo.tcProductIncomingTcStandard ||
                weightInfo.incomingTcStandardDescription ||
                weightInfo.incoming_tc_standard_description ||
                standardsForArticle
              ),
            };
          })
          .filter(
            (entry): entry is {
              incomingProductArticleNo: string;
              incomingProductStandard: string[];
              incomingTcStandardDescription: string[];
            } => Boolean(entry && entry.incomingProductArticleNo)
          );
      }

      if (!derivedInputTcStandard.length) {
        const legacyInputTcStandardList = tcInfo.inputTcStandard || tcInfo.input_tc_standard || [];
        derivedInputTcStandard = Array.isArray(legacyInputTcStandardList)
          ? legacyInputTcStandardList
              .map((item: any) => ({
                incomingProductArticleNo: item.incomingProductArticleNo || item.incoming_product_article_no || "",
                incomingProductStandard: normalizeStringArray(item.incomingProductStandard || item.incoming_product_standard),
                incomingTcStandardDescription: normalizeStringArray(item.incomingTcStandardDescription || item.incoming_tc_standard_description),
              }))
              .filter((entry) => Boolean(entry.incomingProductArticleNo))
          : [];
      }

      if (!Object.keys(standardsByArticle).length && derivedInputTcStandard.length > 0) {
        derivedInputTcStandard.forEach((entry) => {
          if (entry.incomingProductArticleNo) {
            standardsByArticle[entry.incomingProductArticleNo] = entry.incomingProductStandard || [];
          }
        });
      }

      const articleSet = new Set<string>();
      Object.keys(netWeights).forEach((article) => articleSet.add(article));
      Object.keys(standardsByArticle).forEach((article) => articleSet.add(article));
      derivedInputTcStandard.forEach((entry) => {
        if (entry.incomingProductArticleNo) {
          articleSet.add(entry.incomingProductArticleNo);
        }
      });

      let incomingProductArticleNoArr: string[] = Array.from(articleSet);

      if (!incomingProductArticleNoArr.length) {
        const incomingProductArticleNoRaw =
          tcInfo.incomingProductArticleNo ||
          tcInfo.incoming_product_article_no ||
          tcInfo.incomingProductArticleNoList ||
          tcInfo.incomingArticleNo ||
          null;

        if (Array.isArray(incomingProductArticleNoRaw)) {
          incomingProductArticleNoRaw.forEach((x: any) => {
            if (Array.isArray(x)) {
              x.forEach((y: any) => incomingProductArticleNoArr.push(String(y)));
            } else {
              incomingProductArticleNoArr.push(String(x));
            }
          });
        } else if (incomingProductArticleNoRaw) {
          if (typeof incomingProductArticleNoRaw === 'string' && incomingProductArticleNoRaw.includes(',')) {
            incomingProductArticleNoRaw.split(',').forEach((s: string) => incomingProductArticleNoArr.push(s.trim()));
          } else {
            incomingProductArticleNoArr.push(String(incomingProductArticleNoRaw));
          }
        }
      }

      const returnedIncomingTcId = respData.incomingTcId || respData.incoming_tc_id || respData.incomingId || effectiveIncomingTcId || incomingTcId;

      // If the incoming TC number carries a GOTS/ATU token (any position), treat it as ATC
      const isAtcValue = (val: any) => hasCertTypeToken(val, 'GOTS', 'ATU');
      const isFpfValueDetail = (val: any) => hasCertTypeToken(val, 'FPF');

  const rawIncomingNo = tcInfo.tcProductIncomingTcNo || tcInfo.incomingTcNo || tcInfo.incoming_tc_no || '';
  const rawAtcNo = tcInfo.incomingAtcNo || tcInfo.incoming_atc_no || '';
  const rawRmdfNo = tcInfo.incomingRmdfNo || tcInfo.incoming_rmdf_no || '';

      const licensingCodeRaw =
        tcInfo.incomingTcLicensingCode ||
        tcInfo.tcProductIncomingTcCbLicensingCode ||
        tcInfo.incoming_tc_licensing_code ||
        tcInfo.tc_product_incoming_tc_cb_licensing_code ||
        '';

  const incomingIsAtc = isAtcValue(rawIncomingNo);
  const incomingIsRmdf = hasCertTypeToken(rawIncomingNo, 'RMDF');
  const incomingIsFpf = isFpfValueDetail(rawIncomingNo);

  const tcIncomingATCNo = incomingIsAtc ? String(rawIncomingNo) : (rawAtcNo || '');
  const tcIncomingRMDFNo = incomingIsRmdf ? String(rawIncomingNo) : (rawRmdfNo || '');
  const tcIncomingFPFNo = incomingIsFpf ? String(rawIncomingNo) : '';
  const tcIncomingTCNo = (!incomingIsAtc && !incomingIsRmdf && !incomingIsFpf) ? (rawIncomingNo || '') : '';
  const incomingV4Issued = String(tcInfo.tcProductIncomingTcV4Issued || "").toUpperCase();
  const tcIncomingType = incomingIsFpf
    ? "FPF"
    : incomingIsRmdf
    ? "RMDF"
    : incomingIsAtc
    ? (hasCertTypeToken(rawIncomingNo, "GOTS") ? "GOTS" : "ATU")
    : tcIncomingTCNo
    ? (incomingV4Issued === "Y" ? "V4" : "V3")
    : "";

      const sourceStandardsByArticle: Record<string, string[]> = { ...standardsByArticle };
      const sourceByArticle: Record<string, any> = {};
      const sourceRawMaterialsByArticle: Record<string, any[]> = {};
      const sourceMaxWeightByArticle: Record<string, number> = {};
      const sourceIncomingId =
        tcInfo.incomingTcNoId ||
        tcInfo.incoming_tc_no_id ||
        tcInfo.tcProductIncomingTcNoId ||
        tcInfo.tc_product_incoming_tc_no_id ||
        tcInfo.incomingAtcId ||
        tcInfo.incoming_atc_id;

      if (sourceIncomingId && tcIncomingType && incomingProductArticleNoArr.length > 0) {
        await Promise.all(
          incomingProductArticleNoArr.map(async (articleNo) => {
            try {
              const sourceResponse = await executeGet(TCAppConfig.GET_INCOMING_TC_INFO, {
                incomingTcNoTcId: sourceIncomingId,
                tcProductIncomingArticleNo: articleNo,
                incomingTcType: tcIncomingType,
              });
              const sourceRows = unwrapIncomingProducts(sourceResponse?.data?.response ?? sourceResponse?.data);
              const matchingProducts = sourceRows.filter(
                (row: any) => String(row?.tcProductArticleNo || "") === String(articleNo)
              );
              const productsForArticle = matchingProducts.length > 0 ? matchingProducts : sourceRows.slice(0, 1);
              if (productsForArticle.length === 0) return;

              const flattenedRawRows = withIncomingRawMaterialKeys(
                productsForArticle.flatMap((product: any) =>
                  mapIncomingProductToRawRows(product)
                )
              );

              const primaryProduct = productsForArticle[0];
              const sourceStandards = uniqueStrings(
                productsForArticle.reduce<string[]>((acc, product: any) => {
                  acc.push(...normalizeStringArray(product?.standards));
                  acc.push(...normalizeStringArray(product?.tcProductStandard));
                  return acc;
                }, [])
              );
              if (sourceStandards.length > 0) {
                sourceStandardsByArticle[articleNo] = sourceStandards;
              }

              const sourceMaxWeight = toNumber(primaryProduct?.tcProductNetShippingWeight);
              if (sourceMaxWeight > 0) {
                sourceMaxWeightByArticle[articleNo] = sourceMaxWeight;
              }

              if (flattenedRawRows.length > 0) {
                const storedRows = rawMaterials[articleNo] || [];
                const enrichedRows = mergeIncomingRawMaterialsForDisplay(
                  flattenedRawRows,
                  storedRows
                ).map((row: any, index: number) => {
                  const rawMaterialKey = row.rawMaterialKey || getRawMaterialKey(row, index);
                  const storedWeight = Number(row.rawMaterialWeight || 0);
                  if (storedWeight > 0) {
                    rawMaterialWeights[articleNo] = {
                      ...(rawMaterialWeights[articleNo] || {}),
                      [rawMaterialKey]: storedWeight,
                    };
                  }
                  return {
                    ...row,
                    rawMaterialKey,
                    rawMaterialWeight: storedWeight,
                  };
                });
                sourceRawMaterialsByArticle[articleNo] = enrichedRows;
                sourceByArticle[articleNo] = enrichedRows[0];
                rawMaterials[articleNo] = enrichedRows;
              }
            } catch (error) {
              console.error(`Failed to enrich incoming source data for ${articleNo}:`, error);
            }
          })
        );
      }

      incomingProductArticleNoArr.forEach((articleNo) => {
        const storedRows = rawMaterials[articleNo] || [];
        if (!sourceRawMaterialsByArticle[articleNo] && storedRows.length > 0) {
          sourceRawMaterialsByArticle[articleNo] = storedRows;
          sourceByArticle[articleNo] = storedRows[0];
        }
      });

      const inputTcStandard = derivedInputTcStandard;
      const aggregatedStandards = uniqueStrings(
        inputTcStandard.reduce<string[]>((acc, entry) => {
          if (Array.isArray(entry.incomingProductStandard)) {
            acc.push(...entry.incomingProductStandard);
          }
          return acc;
        }, [])
      );
      const sourceAggregatedStandards = uniqueStrings(
        Object.values(sourceStandardsByArticle).reduce<string[]>(
          (acc, standards) => acc.concat(Array.isArray(standards) ? standards : []),
          []
        )
      );

      return {
        id: 0,
        incomingTcId: returnedIncomingTcId,
        productId: respData.productId || respData.product_id || productId,
        incomingTcInfo: {
          tcAssociatedProductArticleNo: tcInfo.tcAssociatedProductArticleNo || tcInfo.tc_associated_product_article_no || "",
          tcIncomingTCNo: tcIncomingTCNo,
          tcIncomingATCNo: tcIncomingATCNo,
          tcIncomingRMDFNo: tcIncomingRMDFNo,
          tcIncomingFPFNo: tcIncomingFPFNo,
          incomingTcLicensingCode: licensingCodeRaw,
          tcIncomingTCStandard: aggregatedStandards.length > 0
            ? aggregatedStandards
            : sourceAggregatedStandards.length > 0
            ? sourceAggregatedStandards
            : normalizeStringArray(tcInfo.incomingTcStandard || tcInfo.incoming_tc_standard),
          tcIncomingTCStandardDescription: normalizeStringArray(
            tcInfo.incomingTcStandardDescription ||
            tcInfo.incoming_tc_standard_description ||
            (aggregatedStandards.length > 0 ? aggregatedStandards : sourceAggregatedStandards)
          ),
          tcIncomingProductNo: incomingProductArticleNoArr as any,
          tcIncomingTcId:
            tcInfo.incomingTcNoId ||
            tcInfo.incoming_tc_no_id ||
            tcInfo.tcProductIncomingTcNoId ||
            tcInfo.tc_product_incoming_tc_no_id ||
            "",
          tcIncomingAtcId: "",
          tcIncomingLedgerTransactionId: "",
          tcIncomingType: tcIncomingType as any,
          tcProductIncomingTcV4Issued: incomingV4Issued === "Y" ? "Y" : incomingV4Issued === "N" ? "N" : "",
          incomingProductStandardsByArticle: sourceStandardsByArticle,
          incomingProductSourceByArticle: sourceByArticle,
          incomingProductRawMaterialsByArticle: sourceRawMaterialsByArticle,
          incomingProductMaxWeightByArticle: sourceMaxWeightByArticle,
          isFlagSeller: (tcInfo.isSellerFarm === 'Yes' || tcInfo.isSeller === 'Yes' || tcInfo.isFlagSeller === 'Yes') ? 'Yes' : 'No',
        },
        productNetWeight: {
          netWeights,
          rawMaterials,
          rawMaterialWeights,
          standards: standardsByArticle,
        },
        inputTcStandard,
      };
    } catch (err) {
      console.error("Error fetching incoming TC detail:", err);
      throw new Error("Failed to load incoming TC detail.");
    }
  }
  

export async function saveIncomingTcDetail(
    executePost: (url: string, payload: any) => Promise<any>,
    tcId: string,
    incomingDetail: IncomingTcEntry,
    oldProductArticleNo: string,
    oldProductId: string,
    sameIncomingTCNewProductCreate: 'Yes' | 'No'
  ): Promise<boolean> {
    try {
    
      const queryParams = new URLSearchParams({
        oldProductId: oldProductId,
        oldProductArticleNo: oldProductArticleNo,
        sameIncomingTCNewProductCreate: sameIncomingTCNewProductCreate,
      });
  
      const url = `${TCAppConfig.POST_INCOMING_DETAIL_V4}?${queryParams.toString()}`;
  
    
  
      const payload: any = {
        tcId,
        productId: incomingDetail.productId || '',
      };
 
      if (incomingDetail.incomingTcId && sameIncomingTCNewProductCreate === 'No') {
        payload.incomingTcId = incomingDetail.incomingTcId;
      }
  

      const standardsMapFromState = incomingDetail.productNetWeight?.standards || {};
      const fallbackStandards = normalizeStringArray(incomingDetail.incomingTcInfo?.tcIncomingTCStandard || []);
      const incomingSourceType = String((incomingDetail.incomingTcInfo as any)?.tcIncomingType || "").toUpperCase();
      const isV3Incoming = incomingSourceType === "V3";
      const rawMaterialWeightsMap = (incomingDetail.productNetWeight as any)?.rawMaterialWeights || {};
      const certifiedRawMaterialsForArticle = (incomingArticleNo: string): any[] => {
        const rows = incomingDetail.productNetWeight.rawMaterials?.[incomingArticleNo] || [];
        return filterCertifiedIncomingRawMaterials(
          withIncomingRawMaterialKeys(rows)
        );
      };
      const certifiedDirectWeightTotalForArticle = (incomingArticleNo: string): number => {
        const directWeightsForArticle = rawMaterialWeightsMap?.[incomingArticleNo] || {};
        return certifiedRawMaterialsForArticle(incomingArticleNo).reduce(
          (sum: number, rm: any) =>
            sum + (Number(directWeightsForArticle[rm.rawMaterialKey]) || 0),
          0
        );
      };

      const netWeightsForSave = incomingDetail.productNetWeight.netWeights || {};
      const productArticleKeys = uniqueStrings([
        ...Object.keys(netWeightsForSave),
        ...Object.keys(rawMaterialWeightsMap),
      ]);
      const netWeightEntries = productArticleKeys
        .filter((incomingArticleNo) => {
          const directTotalWeight = certifiedDirectWeightTotalForArticle(incomingArticleNo);
          const weight = netWeightsForSave[incomingArticleNo];
          return isV3Incoming ? directTotalWeight > 0 || Number(weight) > 0 : weight && Number(weight) > 0;
        })
        .map((incomingArticleNo) => {
          const weight = netWeightsForSave[incomingArticleNo];
          const directWeightsForArticle = rawMaterialWeightsMap?.[incomingArticleNo] || {};
          const directTotalWeight = certifiedDirectWeightTotalForArticle(incomingArticleNo);
          // Preserve the TC v4 five-decimal weight contract in the saved payload.
          const netWeight = isV3Incoming ? roundWeight(Number(weight) || directTotalWeight) : roundWeight(Number(weight));
          const rawMaterials = certifiedRawMaterialsForArticle(incomingArticleNo);
          // Zero-weight rows are dropped instead of sent: a 0 "Incoming Certified
          // Raw Material Weight Used" means the raw material/lot was not used, and
          // the final-JSON validation rejects 0 at submit time (86ajcn0z8).
          const rawMtrlStore = useTransactionCertificateStore.getState().getRawMtrlCodes || [];
          const processedRawMaterials = rawMaterials
            .map((rm: any, index: number) => {
              const percentage = Number(rm.rawMaterialPercentage || 0);
              const directWeight = Number(directWeightsForArticle[rm.rawMaterialKey || getRawMaterialKey(rm, index)] || 0);
              const rawMaterialWeight = isV3Incoming
                ? roundWeight(directWeight)
                : calculateIncomingRmWeight(netWeight, percentage, rm);
              const rawMaterialCode = rm.rawMaterialCode || rm.tcProductIncomingCertifiedRawMaterialCode || "";
              const rawMaterialDesc = rawMtrlStore.find((opt: any) => opt.value === rawMaterialCode)?.label || "";
              return {
                ...rm,
                rawMaterialPercentage: percentage,
                rawMaterialWeight,
                tcProductIncomingCertifiedRawMaterialCode: rawMaterialCode,
                tcProductIncomingCertifiedRawMaterialCodeDescription: rawMaterialDesc,
                tcProductIncomingCertifiedRawMaterialWeightUsed: rawMaterialWeight,
              };
            })
            .filter((rm: any) => Number(rm.tcProductIncomingCertifiedRawMaterialWeightUsed) > 0);

          const standardsFromInput =
            incomingDetail.inputTcStandard?.find((entry) => entry.incomingProductArticleNo === incomingArticleNo)
              ?.incomingProductStandard || [];

          const standardsForArticle = normalizeStringArray(
            standardsMapFromState[incomingArticleNo] || standardsFromInput || fallbackStandards
          );
          const standardCodesForArticle = normalizeStandardCodes(standardsForArticle);

          const categoriesStore = useTransactionCertificateStore.getState().getProductCategoriesStore || [];
          const detailsStore = useTransactionCertificateStore.getState().getProductDetailCodeStore || [];
          const categoryCode = processedRawMaterials[0]?.tcProductCategoryCode || "";
          const detailCode = processedRawMaterials[0]?.tcProductDetailCode || "";
          const categoryDesc = categoriesStore.find((opt: any) => opt.value === categoryCode)?.label || "";
          const detailDesc = detailsStore.find((opt: any) => opt.value === detailCode)?.label || "";

          return {
            tcProductIncomingArticleNo: incomingArticleNo,
            tcProductIncomingArticleNetWeightUsed: netWeight,
            tcProductIncomingTcStandard: standardCodesForArticle,
            tcProductIncomingProductCategoryCode: categoryCode,
            tcProductIncomingProductCategoryCodeDescription: categoryDesc,
            tcProductIncomingProductDetailCode: detailCode,
            tcProductIncomingProductDetailCodeDescription: detailDesc,
            tcProductIncomingRawMaterialInformation: processedRawMaterials,
          };
        });

      const anyDetail: any = incomingDetail;
      const isFlagFromInfo = anyDetail.incomingTcInfo?.isFlagSeller ?? anyDetail.incomingTcInfo?.isSeller;
      const isFlagTop = anyDetail.isFlagSeller ?? anyDetail.isSeller;
      const isSellerFarm = String(isFlagFromInfo ?? isFlagTop ?? 'No') === 'Yes' ? 'Yes' : 'No';

      const incomingTcNoId =
        incomingDetail.incomingTcInfo?.tcIncomingTcId ||
        incomingDetail.incomingTcInfo?.tcIncomingAtcId ||
        '';

      let incomingTcLicensingCode = incomingDetail.incomingTcInfo?.incomingTcLicensingCode || '';
      if (incomingTcLicensingCode && !incomingTcLicensingCode.startsWith('CB-')) {
        incomingTcLicensingCode = `CB-${incomingTcLicensingCode}`;
      }

      if (isSellerFarm === 'Yes') {
        payload.incomingTcProductInformation = [
          {
            tcAssociatedProductArticleNo: incomingDetail.incomingTcInfo.tcAssociatedProductArticleNo,
            tcProductIncomingTcNo: 'na',
            isSellerFarm: 'Yes',
            incomingTcNoId: 'na',
            incomingTcLicensingCode: 'na',
            incomingTcProductNetWeightUsedInformation: [
              {
                tcProductIncomingArticleNo: 'na',
                tcProductIncomingArticleNetWeightUsed: 0,
                tcProductIncomingTcStandard: ['na'],
                tcProductIncomingRawMaterialInformation: [],
              },
            ],
          },
        ];
      } else {
        payload.incomingTcProductInformation = [
          {
            tcAssociatedProductArticleNo: incomingDetail.incomingTcInfo.tcAssociatedProductArticleNo,
            tcProductIncomingTcNo:
              incomingDetail.incomingTcInfo.tcIncomingTCNo ||
              incomingDetail.incomingTcInfo.tcIncomingATCNo ||
              incomingDetail.incomingTcInfo.tcIncomingRMDFNo ||
              incomingDetail.incomingTcInfo.tcIncomingFPFNo ||
              '',
            tcProductIncomingTcV4Issued:
              incomingDetail.incomingTcInfo.tcProductIncomingTcV4Issued ||
              (incomingSourceType === 'V4' ? 'Y' : 'N'),
            tcProductIncomingTcCbLicensingCode: incomingTcLicensingCode,
            isSellerFarm: 'No',
            incomingTcNoId: incomingTcNoId,
            incomingTcLicensingCode: incomingTcLicensingCode,
            incomingTcProductNetWeightUsedInformation: netWeightEntries,
          },
        ];
      }
  
      
  
      const response = await executePost(url, payload);
  
      if (response?.status === 200) {
        return true;
      }
      
      console.error("Save failed:", response);
      return false;
      
    } catch (err: any) {
      console.error(" Error saving incoming TC detail:", err);
      throw new Error(err?.response?.data?.message || "Failed to save incoming TC detail.");
    }
  }
  
export async function deleteIncomingTcDetail(
    executeDelete: (
      url: string,
      params?: Record<string, any>,
      config?: Record<string, any>
    ) => Promise<any>,
    tcId: string,
    incomingTcId: any,
    incomingTcNo: string,
    productId: any,
    productArticleNo: string
  ): Promise<any> {
    try {
      const params: Record<string, any> = {
        tcId,
        incomingTCId: String(incomingTcId),
        incomingTCNo: incomingTcNo,
        productId,
        productArticleNo,
      };

      const response = await executeDelete(`${TCAppConfig.TC_DELETE_API_V4}`, params);
      return response;
    } catch (err) {
      console.error("Error deleting incoming TC detail:", err);
      throw new Error("Failed to delete incoming TC detail.");
    }
  }
